/**
 * Finalization Shared Utilities
 * BucketHeader, EmptyBucket, getBucketCount, getBucketStatus,
 * DiagnosisLinkRow, DxPickerDropdown, FinalizationSummary,
 * ReconciliationPanel, FINALIZATION_BUCKETS
 */
import React from 'react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Code2, Pill, TestTubes, ImageIcon, Zap, ShieldCheck, ClipboardList,
  Check, Clock, AlertCircle, ArrowRight, X, Plus, Loader2
} from 'lucide-react';

// ── Bucket Config ─────────────────────────────────────────────────────────────

export const FINALIZATION_BUCKETS = [
  { key: 'prescriptions', label: 'Prescriptions',    icon: Pill,           color: 'bg-violet-50 border-violet-200 text-violet-700' },
  { key: 'labs',          label: 'Labs',              icon: TestTubes,      color: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
  { key: 'imaging',       label: 'Imaging',           icon: ImageIcon,      color: 'bg-amber-50 border-amber-200 text-amber-700' },
  { key: 'actions',       label: 'Actions',           icon: Zap,            color: 'bg-sky-50 border-sky-200 text-sky-700' },
  { key: 'aco',           label: 'ACO / Quality',     icon: ShieldCheck,    color: 'bg-teal-50 border-teal-200 text-teal-700' },
  { key: 'chart_updates', label: 'Chart Updates',     icon: ClipboardList,  color: 'bg-orange-50 border-orange-200 text-orange-700' },
  { key: 'coding',        label: 'Coding',            icon: Code2,          color: 'bg-blue-50 border-blue-200 text-blue-700' },
];

// ── Shared Display Components ─────────────────────────────────────────────────

export function BucketHeader({ icon: Icon, title, count, status }) {
  return (
    <div className="flex items-center gap-2">
      <div className={`p-1.5 rounded-lg ${status === 'complete' ? 'bg-emerald-100' : 'bg-muted'}`}>
        <Icon className={`w-4 h-4 ${status === 'complete' ? 'text-emerald-600' : 'text-muted-foreground'}`} />
      </div>
      <h3 className="text-base font-semibold text-foreground" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
        {title}
      </h3>
      {count > 0 && <Badge variant="outline" className="text-[10px]">{count}</Badge>}
      {status === 'complete' && (
        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[9px] gap-0.5">
          <Check className="w-2.5 h-2.5" /> Complete
        </Badge>
      )}
    </div>
  );
}

export function EmptyBucket({ icon: Icon, message }) {
  return (
    <div className="text-center py-12">
      <div className="p-3 rounded-2xl bg-muted/50 mx-auto w-fit mb-3">
        <Icon className="w-6 h-6 text-muted-foreground" />
      </div>
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

// ── Bucket Count / Status Helpers ─────────────────────────────────────────────

export function getBucketCount(key, enc, outputs, qualityOverview) {
  const chartUpdates = outputs?.chart_updates || [];
  const actions = enc?.suggested_actions || [];

  switch (key) {
    case 'coding': {
      const billing = outputs?.billing_suggestion;
      return [billing?.primary_code, ...(billing?.diagnosis_codes || [])].filter(Boolean).length;
    }
    case 'prescriptions':
      return chartUpdates.filter(cu => cu.update_type?.startsWith('medication')).length;
    case 'labs':
      return actions.filter(a => a.action_type === 'screening' || a.action_type === 'lab_order' ||
        a.action_name?.toLowerCase().includes('lab') || a.action_name?.toLowerCase().includes('screen')).length
        + (enc?.lab_orders || []).length;
    case 'imaging':
      return actions.filter(a => a.action_type === 'imaging' || a.action_name?.toLowerCase().includes('x-ray') ||
        a.action_name?.toLowerCase().includes('mri')).length
        + (enc?.imaging_orders || []).length;
    case 'actions':
      return actions.filter(a => ['task','injection','form','referral','follow_up','screening'].includes(a.action_type)).length;
    case 'chart_updates': {
      // Count problem signals pending + med changes — approximate from billing codes
      const billing = outputs?.billing_suggestion;
      const dxCount = (billing?.diagnosis_codes || []).length;
      const medCount = chartUpdates.filter(cu => cu.update_type?.startsWith('medication') && (!cu.disposition || cu.disposition === 'unreviewed')).length;
      return dxCount + medCount;
    }
    case 'aco':
      return (qualityOverview?.header?.total_gap_count || 0) +
        chartUpdates.filter(cu => cu.update_type === 'preventive_update' || cu.update_type === 'preventive_prompt').length;
    default: return 0;
  }
}

export function getBucketStatus(key, enc, outputs, qualityOverview) {
  const chartUpdates = outputs?.chart_updates || [];
  const actions = enc?.suggested_actions || [];

  switch (key) {
    case 'coding': {
      const billing = outputs?.billing_suggestion;
      if (!billing?.primary_code) return 'empty';
      return (billing.approval_status === 'approved' || billing.approval_status === 'approve') ? 'complete' : 'pending';
    }
    case 'prescriptions': {
      const meds = chartUpdates.filter(cu => cu.update_type?.startsWith('medication'));
      if (meds.length === 0) return 'empty';
      const HANDLED = ['approve', 'sent', 'queued', 'reject', 'added_to_list'];
      return meds.every(m => HANDLED.includes(m.disposition)) ? 'complete' : 'pending';
    }
    case 'labs': {
      const labs = actions.filter(a => a.action_type === 'screening' || a.action_type === 'lab_order' ||
        (a.action_name || '').toLowerCase().includes('lab') || (a.action_name || '').toLowerCase().includes('screen'));
      if (labs.length === 0) return 'empty';
      if (!labs.every(a => a.status !== 'pending')) return 'pending';
      const executed = labs.filter(a => a.status !== 'dismissed');
      return executed.every(a => a.linked_diagnosis) ? 'complete' : 'pending';
    }
    case 'imaging': {
      const imgs = actions.filter(a => a.action_type === 'imaging' || (a.action_name || '').toLowerCase().includes('x-ray') ||
        (a.action_name || '').toLowerCase().includes('mri'));
      if (imgs.length === 0) return 'empty';
      return imgs.every(a => a.status !== 'pending') ? 'complete' : 'pending';
    }
    case 'actions': {
      const tasks = actions.filter(a => ['task','injection','form','referral','follow_up','screening'].includes(a.action_type));
      if (tasks.length === 0) return 'empty';
      return tasks.every(a => a.status !== 'pending') ? 'complete' : 'pending';
    }
    case 'chart_updates': {
      // Approximate: 'pending' if there are billing diagnosis codes (real count comes from the component)
      const billing = outputs?.billing_suggestion;
      const dxCount = (billing?.diagnosis_codes || []).length;
      if (dxCount === 0) return 'empty';
      return 'pending'; // Always prompt review — component self-manages applied state
    }
    case 'aco': {
      const preventive = chartUpdates.filter(cu => cu.update_type === 'preventive_update' || cu.update_type === 'preventive_prompt');
      const gaps = qualityOverview?.header?.total_gap_count || 0;
      if (preventive.length === 0 && gaps === 0) return 'empty';
      return preventive.every(p => p.disposition && p.disposition !== 'unreviewed') ? 'complete' : 'pending';
    }
    default: return 'empty';
  }
}

// ── Finalization Summary (bottom bar progress) ────────────────────────────────

export function FinalizationSummary({ enc, outputs, qualityOverview }) {
  const ps = enc?.proposal_summary || {};
  const total = ps.total_pending || 0;

  const allBuckets = FINALIZATION_BUCKETS.map(b => ({
    key: b.key,
    count: getBucketCount(b.key, enc, outputs, qualityOverview),
    status: getBucketStatus(b.key, enc, outputs, qualityOverview),
  }));
  const active = allBuckets.filter(b => b.count > 0);
  const complete = active.filter(b => b.status === 'complete');
  const pct = active.length > 0 ? Math.round((complete.length / active.length) * 100) : 100;

  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2">
        <div className="w-24 h-1.5 rounded-full bg-muted overflow-hidden" data-testid="finalize-progress-bar">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${pct}%`, backgroundColor: pct === 100 ? 'hsl(var(--chart-2))' : 'hsl(var(--primary))' }}
          />
        </div>
        <span className="text-[10px] text-muted-foreground tabular-nums">{complete.length}/{active.length}</span>
      </div>
      {total === 0 ? (
        <div className="flex items-center gap-1.5 text-xs text-emerald-700">
          <Check className="w-3.5 h-3.5" /> Ready to sign
        </div>
      ) : (
        <div className="flex items-center gap-1.5 text-xs">
          <Clock className="w-3.5 h-3.5 text-amber-500" />
          <span className="text-amber-700 font-medium">{total} pending</span>
        </div>
      )}
    </div>
  );
}

// ── Reconciliation Panel ──────────────────────────────────────────────────────

export function ReconciliationPanel({ report, acceptedUpdates, setAcceptedUpdates, onApplyAndFinalize, onSkipAndFinalize, onCancel, finalizing }) {
  const discrepancies = report?.discrepancies || [];
  const updates = report?.proposed_note_updates || [];
  const summary = report?.summary || {};

  const TYPE_LABELS = {
    dropped:       { label: 'Dismissed in Note',  color: 'bg-red-50 text-red-700 border-red-200' },
    added:         { label: 'Missing from Note',   color: 'bg-amber-50 text-amber-700 border-amber-200' },
    modified:      { label: 'Unreviewed',           color: 'bg-sky-50 text-sky-700 border-sky-200' },
    orphan:        { label: 'No Diagnosis Link',    color: 'bg-violet-50 text-violet-700 border-violet-200' },
    linkage_issue: { label: 'Linkage Issue',        color: 'bg-violet-50 text-violet-700 border-violet-200' },
  };

  const toggleUpdate = (id) =>
    setAcceptedUpdates(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  return (
    <div className="border-t bg-amber-50/30" data-testid="reconciliation-panel">
      <div className="px-5 py-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600" />
            <h3 className="text-sm font-semibold text-foreground" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Reconciliation Review
            </h3>
            <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
              {discrepancies.length} discrepanc{discrepancies.length === 1 ? 'y' : 'ies'}
            </Badge>
          </div>
          <button onClick={onCancel} className="text-xs text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>

        <p className="text-xs text-muted-foreground mb-3">{summary.message}</p>

        <div className="space-y-2 max-h-48 overflow-y-auto mb-3">
          {discrepancies.map(disc => {
            const typeCfg = TYPE_LABELS[disc.type] || TYPE_LABELS.modified;
            const relatedUpdate = updates.find(u => u.discrepancy_id === disc.id);
            const isAccepted = relatedUpdate && acceptedUpdates.includes(relatedUpdate.id);
            return (
              <div key={disc.id} className="rounded-md border bg-card p-2.5" data-testid={`recon-disc-${disc.id}`}>
                <div className="flex items-start gap-2">
                  <Badge variant="outline" className={`text-[9px] h-4 px-1.5 shrink-0 mt-0.5 ${typeCfg.color}`}>
                    {typeCfg.label}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-foreground">{disc.description}</p>
                    <div className="flex items-center gap-3 mt-1 text-[10px] text-muted-foreground">
                      <span>Draft: {disc.draft_state}</span>
                      <ArrowRight className="w-3 h-3" />
                      <span>Final: {disc.final_state}</span>
                    </div>
                  </div>
                  {relatedUpdate && (
                    <label className="flex items-center gap-1 shrink-0 cursor-pointer" data-testid={`recon-toggle-${relatedUpdate.id}`}>
                      <input type="checkbox" checked={isAccepted} onChange={() => toggleUpdate(relatedUpdate.id)} className="rounded border-border" />
                      <span className="text-[9px] text-muted-foreground">Update note</span>
                    </label>
                  )}
                </div>
                {relatedUpdate && (
                  <div className="mt-1.5 pl-6 text-[10px] text-muted-foreground italic">
                    {relatedUpdate.action === 'add_plan_item' && `Add to plan: "${relatedUpdate.plan_item_text}"`}
                    {relatedUpdate.action === 'remove_plan_item' && `Remove from plan: "${relatedUpdate.plan_item_text}"`}
                    {relatedUpdate.action === 'modify_plan_item' && `Update: "${relatedUpdate.old_text}" → "${relatedUpdate.new_text}"`}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-amber-200/50">
          <p className="text-[10px] text-muted-foreground">
            {acceptedUpdates.length} of {updates.length} note update{updates.length !== 1 ? 's' : ''} selected
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={onSkipAndFinalize} disabled={finalizing} data-testid="recon-skip-finalize">
              Skip & Finalize Anyway
            </Button>
            <Button size="sm" className="h-8 text-xs gap-1" onClick={onApplyAndFinalize} disabled={finalizing} data-testid="recon-apply-finalize">
              {finalizing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              {acceptedUpdates.length > 0 ? 'Apply Updates & Finalize' : 'Finalize'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Diagnosis Link Row ────────────────────────────────────────────────────────

export function DiagnosisLinkRow({ linked, problems, diagnosisCodes, onLink }) {
  const [showPicker, setShowPicker] = React.useState(false);

  const available = React.useMemo(() => {
    const items = [];
    (problems || []).forEach((p, i) => {
      if (p.icd_code || p.problem_name) items.push({ code: p.icd_code || `P${i + 1}`, label: p.problem_name || p.icd_code });
    });
    (diagnosisCodes || []).forEach(code => {
      if (!items.find(it => it.code === code)) items.push({ code, label: code });
    });
    return items;
  }, [problems, diagnosisCodes]);

  if (linked) {
    return (
      <div className="flex items-center gap-1.5 mt-1.5" data-testid="dx-link-row">
        <span className="w-2 h-2 rounded-full bg-emerald-400" />
        <Badge variant="outline" className="text-[9px] font-mono bg-emerald-50 text-emerald-700 border-emerald-200">{linked}</Badge>
        <span className="text-[9px] text-emerald-600">linked</span>
        <button onClick={() => setShowPicker(!showPicker)} className="text-[9px] text-muted-foreground hover:text-foreground ml-1" data-testid="dx-link-change">change</button>
        {showPicker && <DxPickerDropdown items={available} onSelect={code => { onLink(code); setShowPicker(false); }} onClose={() => setShowPicker(false)} />}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 mt-1.5" data-testid="dx-link-row">
      <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
      <span className="text-[9px] text-amber-600 font-medium">No diagnosis linked</span>
      {available.length > 0 ? (
        <div className="relative">
          <button onClick={() => setShowPicker(!showPicker)} className="text-[9px] text-primary hover:text-primary/80 font-medium flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-primary/20 hover:border-primary/40 transition-colors" data-testid="dx-link-fix-btn">
            <Plus className="w-2.5 h-2.5" /> Link
          </button>
          {showPicker && <DxPickerDropdown items={available} onSelect={code => { onLink(code); setShowPicker(false); }} onClose={() => setShowPicker(false)} />}
        </div>
      ) : (
        <span className="text-[9px] text-muted-foreground">No diagnoses available</span>
      )}
    </div>
  );
}

function DxPickerDropdown({ items, onSelect, onClose }) {
  return (
    <div className="absolute left-0 top-full mt-1 z-20 w-56 bg-card border rounded-lg shadow-lg p-1 animate-in fade-in-0 slide-in-from-top-1 duration-100" data-testid="dx-picker-dropdown">
      <div className="px-2 py-1 border-b mb-1">
        <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Select Diagnosis</p>
      </div>
      {items.map(item => (
        <button key={item.code} onClick={() => onSelect(item.code)} className="w-full text-left px-2 py-1.5 rounded text-[10px] hover:bg-accent transition-colors flex items-center gap-2" data-testid={`dx-pick-${item.code}`}>
          <Badge variant="outline" className="text-[8px] font-mono shrink-0">{item.code}</Badge>
          <span className="truncate text-foreground">{item.label}</span>
        </button>
      ))}
      {items.length === 0 && <p className="text-[10px] text-muted-foreground text-center py-2">No diagnoses found</p>}
    </div>
  );
}
